import React, { useState, useEffect } from 'react';
import { Search, X, Zap, Gift, DollarSign, Bookmark, Users, LogIn, ChevronDown, User, Package, Printer, ChevronRight } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue } from 'firebase/database';

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyARv3RMmZCBVru7_sjwxNDt9HO5PxbAtx4",
  authDomain: "cheeks-live-status.firebaseapp.com",
  databaseURL: "https://cheeks-live-status-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "cheeks-live-status",
  storageBucket: "cheeks-live-status.firebasestorage.app",
  messagingSenderId: "743249460847",
  appId: "1:747249460847:web:46f2d8835c19ca8effd79a"
};

// Initialise Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

const CardExplorer = () => {
  const [isLive, setIsLive] = useState(false);
  const [memberCount, setMemberCount] = useState(null);
  const [voiceCount, setVoiceCount] = useState(0);
  const [instagramCount, setInstagramCount] = useState(null);
  const [whatnotCount, setWhatnotCount] = useState(null);
  const [bannerText, setBannerText] = useState('');
  const [myStatus, setMyStatus] = useState('');
  const [myPresence, setMyPresence] = useState(null);
  const [myBadges, setMyBadges] = useState([]);
  const [myActivity, setMyActivity] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [discordUser, setDiscordUser] = useState(() => {
    try {
      const saved = localStorage.getItem('discordUser');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loginErrorVisible, setLoginErrorVisible] = useState(false);
  const [showPogoModal, setShowPogoModal] = useState(false);
  const [showHearts, setShowHearts] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [funFact, setFunFact] = useState('');

  useEffect(() => {
    fetch('https://pokefacts.vercel.app/')
      .then(r => r.json())
      .then(d => setFunFact(d.data?.[0] || ''))
      .catch(() => {});
  }, []);

  // Badge definitions — keyed by role name, with image path and display label

  useEffect(() => {
    const t = setTimeout(() => { setShowHearts(true); }, 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setLoginErrorVisible(true);
    const fade = setTimeout(() => setLoginErrorVisible(false), 4500);
    const clear = setTimeout(() => setLoginError(false), 5000);
    return () => { clearTimeout(fade); clearTimeout(clear); };
  }, [loginError]);

  // Tab state — 'profile', 'inventory', or 'creations'
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '');
    return ['profile', 'inventory', 'creations'].includes(hash) ? hash : 'profile';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (['profile', 'inventory', 'creations'].includes(hash)) {
        setActiveTab(hash);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const [expandedInstructions, setExpandedInstructions] = useState(null);
  const [expandedFirmware, setExpandedFirmware] = useState(null);
  const [firmwareState, setFirmwareState] = useState('disconnected');
  const [firmwareProgress, setFirmwareProgress] = useState(0);
  const [serialPort, setSerialPort] = useState(null);
  const [serialWriter, setSerialWriter] = useState(null);
  const [deviceVersion, setDeviceVersion] = useState(null);
  const [deviceName, setDeviceName] = useState(null);
  const LATEST_VERSION = 'v1.2.4';
  const OTA_URL = 'https://cheeks.uk/firmware/latest.bin';

  const readLines = async (reader, onLine, timeoutMs = 4000) => {
    let buf = '';
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += new TextDecoder().decode(value);
      const parts = buf.split('\n');
      buf = parts.pop();
      for (const line of parts) {
        const trimmed = line.trim();
        if (trimmed) { const stop = onLine(trimmed); if (stop) return; }
      }
    }
  };

  const connectSerial = async () => {
    if (!('serial' in navigator)) { setFirmwareState('unsupported'); return; }
    try {
      const port = await navigator.serial.requestPort({ filters: [{ usbVendorId: 0x303a }] });
      await port.open({ baudRate: 115200 });
      setFirmwareState('connecting');
      const writer = port.writable.getWriter();
      const reader = port.readable.getReader();
      await writer.write(new TextEncoder().encode('VERSION\n'));
      let ver = 'v1.0.0', name = 'Digital Display Stand';
      await readLines(reader, (line) => {
        if (line.startsWith('VERSION:')) ver = line.replace('VERSION:', '').trim();
        if (line.startsWith('NAME:')) name = line.replace('NAME:', '').trim();
        if (line === 'OK') return true;
      }, 3000);
      reader.releaseLock();
      setSerialPort(port);
      setSerialWriter(writer);
      setDeviceVersion(ver);
      setDeviceName(name);
      setFirmwareState('connected');
    } catch(e) {
      if (e.name !== 'NotFoundError') setFirmwareState('disconnected');
    }
  };

  const disconnectSerial = async () => {
    try { if (serialWriter) serialWriter.releaseLock(); } catch(e) {}
    try { if (serialPort) await serialPort.close(); } catch(e) {}
    setSerialPort(null); setSerialWriter(null);
    setDeviceVersion(null); setDeviceName(null);
    setFirmwareState('disconnected');
  };

  const runOTA = async () => {
    if (!serialPort || !serialWriter) return;
    setFirmwareState('updating');
    setFirmwareProgress(0);
    try {
      await serialWriter.write(new TextEncoder().encode(`OTA:${OTA_URL}\n`));
      const reader = serialPort.readable.getReader();
      await readLines(reader, (line) => {
        if (line.startsWith('OTA_PROGRESS:')) {
          const pct = parseInt(line.replace('OTA_PROGRESS:', ''));
          setFirmwareProgress(pct);
        } else if (line === 'OTA_DONE') {
          setFirmwareProgress(100);
          setFirmwareState('done');
          return true;
        } else if (line.startsWith('OTA_ERROR:')) {
          const msg = line.replace('OTA_ERROR:', '');
          console.error('OTA error:', msg);
          setFirmwareState('error');
          return true;
        }
      }, 120000); // 2 min timeout for the actual flash
      reader.releaseLock();
    } catch(e) {
      console.error('OTA exception:', e);
      setFirmwareState('error');
    }
  };
  const [displayImageIndex, setDisplayImageIndex] = useState(0);
  const [selectedStandImage, setSelectedStandImage] = useState('/digital_stand.png');
  const [selectedInventoryImage, setSelectedInventoryImage] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayImageIndex(i => (i + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const switchTab = (tab) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const bannerBubbleRef = React.useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [images, setImages] = useState([]);

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const examples = ['Pikachu', 'Giratina', 'Sunflora'];

  const categories = [
    { id: 'all', name: 'All Items', folder: null },
    { id: 'tcg', name: 'Pokemon TCG', folder: 'TCG' },
    { id: 'nontcg', name: 'Non-TCG', folder: 'nonTCG' },
  ];

  const handleDiscordLogin = () => {
    const CLIENT_ID = '1360587565190746193';
    const REDIRECT_URI = encodeURIComponent(window.location.origin);
    const SCOPE = encodeURIComponent('identify guilds');
    const AUTH_URL = `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=${SCOPE}`;
    window.location.href = AUTH_URL;
  };

  const handleLogout = () => {
    localStorage.removeItem('discordUser');
    setDiscordUser(null);
    setShowUserMenu(false);
  };

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-user-menu]')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.replace('#', '?'));
      const token = params.get('access_token');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetch('https://discord.com/api/users/@me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(userData => {
          return fetch('https://discord.com/api/users/@me/guilds', {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(guilds => {
              const isMember = guilds.some(g => g.id === '1466528743265931459');
              if (isMember) {
                localStorage.setItem('discordUser', JSON.stringify(userData));
                setDiscordUser(userData);
              } else {
                setLoginError(true);
              }
            });
        })
        .catch(err => console.error("Login failed:", err));
    }
  }, []);

  useEffect(() => {
    const liveRef = ref(database, 'isLive');
    const unsubscribe = onValue(liveRef, (snapshot) => setIsLive(snapshot.val() === true));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const memberRef = ref(database, 'memberCount');
    const unsubscribe = onValue(memberRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setMemberCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const voiceRef = ref(database, 'voiceCount');
    const unsubscribe = onValue(voiceRef, (snapshot) => setVoiceCount(snapshot.val() || 0));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const igRef = ref(database, 'instagramCount');
    const unsubscribe = onValue(igRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setInstagramCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const wnRef = ref(database, 'whatnotCount');
    const unsubscribe = onValue(wnRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setWhatnotCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const bannerRef = ref(database, 'bannerText');
    const unsubscribe = onValue(bannerRef, (snapshot) => setBannerText(snapshot.val() || ''));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const statusRef = ref(database, 'myStatus');
    const unsubscribe = onValue(statusRef, (snapshot) => {
      setMyStatus(snapshot.val() || '');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const presenceRef = ref(database, 'myPresence');
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      setMyPresence(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const badgesRef = ref(database, 'myBadges');
    const unsubscribe = onValue(badgesRef, (snapshot) => {
      setMyBadges(snapshot.val() || []);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const activityRef = ref(database, 'myActivity');
    const unsubscribe = onValue(activityRef, (snapshot) => {
      setMyActivity(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);


  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!myActivity?.startedAt) { setElapsed(''); return; }
    const tick = () => {
      const secs = Math.floor((Date.now() - myActivity.startedAt) / 1000);
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const sc = secs % 60;
      setElapsed(h > 0
        ? `${h}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`
        : `${m}:${String(sc).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [myActivity?.startedAt]);

  const formatCount = (count) => {
    if (!count) return null;
    return count.toLocaleString();
  };

  useEffect(() => {
    // TCG Images
const tcgFileList = [
  'Articuno Holo - Fossil - Japanese.png',
  'Blissey Holo - Neo Revelation - Japanese.png',
  'Charizard EX SIR - Pokemon 151 - English.png',
  'Charizard Rainbow - Brilliant Stars - English.png',
  'Charizard Rev Holo - Premium File Promo - Japanese.png',
  'Dark Raichu Holo - Team Rocket WOTC - English.png',
  'Eevee EX Promo - Prismatic Evolutions UPC - English.png',
  'Eevee EX SAR - Terastal Festival - Japanese.png',
  'Entei Rev Holo - Premium File Promo - Japanese.png',
  'Felt Hat Pikachu - Promo - English.png',
  'Gengar VMAX Alt Art 1 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 2 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 3 - Fusion Strike - English.png',
  'Giratina V Alt Art - Lost Origin - English.png',
  'Haunter Holo - Fossil - Japanese.png',
  'Lugia Holo - Neo Genesis - Japanese.png',
  'Lugia V Alt Art - Silver Tempest - English.png',
  'Mega Charizard EX - Inferno X - Japanese.png',
  'Mega Charizard EX Promo 1 - Charizard UPC - English.png',
  'Mega Charizard EX Promo 2 - Charizard UPC - English.png',
  'Mega Charizard EX SIR - Phantasmal Flames - English.png',
  'Mew EX - Pokemon 151 - Japanese.png',
  'Mew Holo - WOTC Promo - English.png',
  'Moltres Holo - Fossil - Japanese.png',
  'Moltres V Alt Art - Chilling Reign - English.png',
  'Pichu Rev Holo - Premium File Promo - Japanese.png',
  'Rayquaza VMAX 1 - Silver Tempest - English.png',
  'Rayquaza VMAX 2 - Silver Tempest - English.png',
  'Spiky Eared Pichu Holo - Legend of Arceus Promo - Japanese.png',
  'Togepi Rev Holo - Southern Islands Promo - Japanese.png',
  'Umbreon VMAX Alt Art - Evolving Skies - English.png',
];

    // Non-TCG Images (plushies, figures, sealed products, etc.)
const nonTCGFileList = [
  'Batomen - Series 1 - TOMY.jpg',
  'Batomen - Series 2 - TOMY.jpg',
  'Batomen - Series 3 - TOMY.jpg',
  'Battle Memo - Part 1 - TOMY.jpg',
  'Battle Memo - Part 2 - TOMY.jpg',
  'Battle Memo - Part 3 - TOMY.jpg',
  'Blastoise  - Batomen - TOMY.jpg',
  'Charizard - Batomen - TOMY.jpg',
  'Charmander - Topsun Blue Back - Japanese.png',
  'Coonuts - Series 3 - Bandai.jpg',
  'Gengar Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Mew Holo - Batomen - TOMY.jpg',
  'Mewtwo Holo - Batomen - TOMY.jpg',
  'Pikachu - Pixel Coaster - Pokemon Center Japan.jpg',
  'Pikachu - Pokepeace - Plushy Notepad.jpg',
  'Pikachu & Eevee - PokeGET.jpg',
  'Snorlax - Bandai Anime - Carddass.jpg',
  'Umbreon Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Venusaur  - Batomen - TOMY.jpg',
];

    const tcgImages = tcgFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const gradingCompanies = ['PSA', 'BGS', 'ACE', 'CGC'];
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      } else {
        for (const company of gradingCompanies) {
          if (firstPart.toUpperCase().startsWith(company)) {
            const gradeMatch = firstPart.match(new RegExp(`${company}\\s*([0-9.]+)\\s*(.*)`, 'i'));
            if (gradeMatch) {
              gradeLabel = `${company} ${gradeMatch[1]}`;
              parts[0] = gradeMatch[2].trim();
              if (!parts[0]) parts.shift();
            }
            break;
          }
        }
      }
      const cardName = parts[0] || 'Unknown Card';
      const setLines = parts.slice(1);
      return { id: index + 1, name: cardName, set: setLines.join('  '), filename, category: 'tcg', grade: gradeLabel, folder: 'TCG' };
    });

    const nonTCGImages = nonTCGFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      }
      const itemName = parts[0] || 'Unknown Item';
      const itemLines = parts.slice(1);
      return { id: tcgImages.length + index + 1, name: itemName, set: itemLines.join('  '), filename, category: 'nontcg', grade: gradeLabel, folder: 'nonTCG' };
    });

    setImages([...tcgImages, ...nonTCGImages]);
  }, []);

  useEffect(() => {
    const currentExample = examples[currentExampleIndex];
    if (isTyping) {
      if (currentText.length < currentExample.length) {
        const timeout = setTimeout(() => setCurrentText(currentExample.substring(0, currentText.length + 1)), 100);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setIsTyping(false), 2000);
        return () => clearTimeout(timeout);
      }
    } else {
      if (currentText.length > 0) {
        const timeout = setTimeout(() => setCurrentText(currentText.substring(0, currentText.length - 1)), 50);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % examples.length);
          setIsTyping(true);
        }, 500);
        return () => clearTimeout(timeout);
      }
    }
  }, [currentText, isTyping, currentExampleIndex]);

  useEffect(() => {
    const video = document.createElement('video');
    video.src = '/splash.mp4';
    video.preload = 'auto';
    video.load();
  }, []);

  // Prevent right-click / long-press saving of images
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      img { -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; pointer-events: none; }
      a img, button img { pointer-events: auto; }
      * { -webkit-touch-callout: none; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (activeTab !== 'profile') return;
    const container = bannerBubbleRef.current;
    if (!container) return;
    const bubbles = [];
    for (let i = 0; i < 22; i++) {
      const b = document.createElement('div');
      b.className = 'banner-bubble';
      const size = Math.random() * 20 + 12;
      b.style.width = `${size}px`;
      b.style.height = `${size}px`;
      b.style.left = `${Math.random() * 100}%`;
      b.style.animationDelay = `-${Math.random() * 8}s`;
      b.style.animationDuration = `${Math.random() * 4 + 5}s`;
      b.style.setProperty('--bdrift', Math.random() * 2 - 1);
      container.appendChild(b);
      bubbles.push(b);
    }
    return () => bubbles.forEach(b => b.remove());
  }, [activeTab]);

  const handleVideoEnd = () => setVideoEnded(true);
  const handleVideoModalClose = () => {
    setShowVideoModal(false);
    setVideoEnded(false);
  };

  const handleSearch = () => { /* filter is reactive */ };

  const filteredImages = images.filter(img => {
    if (!searchQuery) return selectedCategory === 'all' || img.category === selectedCategory;
    const searchWords = searchQuery.toLowerCase().trim().split(/\s+/);
    const searchableText = [img.name, img.set, img.filename, img.grade || ''].join(' ').toLowerCase();
    const matchesSearch = searchWords.every(word => searchableText.includes(word));
    const matchesCategory = selectedCategory === 'all' || img.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ── Profile link data ──────────────────────────────────────────────
  const profileLinks = [
    {
      label: 'Discord',
      sub: 'discord.cheeks.uk',
      href: 'https://pokebubble.com/docs',
      color: '#5865F2',
      bgColor: 'rgba(88,101,242,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057c.001.022.015.043.032.054a19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/>
        </svg>
      )
    },
    {
      label: 'Instagram',
      sub: '@togecheeks',
      href: 'https://www.instagram.com/togecheeks',
      color: '#E1306C',
      bgColor: 'rgba(225,48,108,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      )
    },
    {
      label: 'Whatnot — Live Auctions',
      sub: 'Watch & bid live',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      isButton: true,
      icon: <Gift className="h-5 w-5" />
    },
    {
      label: 'Get £10 FREE on Whatnot',
      sub: 'New buyer promo',
      href: 'https://whatnot.com/invite/togecheeks',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <DollarSign className="h-5 w-5" />
    },
    {
      label: 'Bookmark Next Livestreams',
      sub: 'Never miss a drop',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <Bookmark className="h-5 w-5" />
    },
  ];

  const badgeMap = {
    diamond: { img: '/diamond.png', label: 'Diamond' },
  };

  const badges = (Array.isArray(myBadges) ? myBadges : Object.keys(myBadges || {}))
    .map(key => badgeMap[key])
    .filter(Boolean);

  return (
    <div
      className="bg-slate-900 flex flex-col"
      style={{ minHeight: '100svh' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >

      {/* ── BANNER ── */}
      {bannerText && (
        <div className="relative z-50 flex items-center justify-center py-2 px-4 bg-slate-800/80 backdrop-blur-lg border-b border-slate-700/50">
          <div className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full px-4 py-1.5 shadow-sm">
            <img src="/pball.svg" alt="" className="h-3 w-3 animate-spin flex-shrink-0" style={{animationDuration:'0.8s', filter:'brightness(0) invert(1)'}} />
            <span className="text-white text-[11px] font-medium tracking-wide leading-none">
              {(() => {
                const linkRegex = /\[(.*?)\]\((.*?)\)/;
                const match = bannerText.match(linkRegex);
                if (match) {
                  const [fullMatch, label, url] = match;
                  const [before, after] = bannerText.split(fullMatch);
                  return (
                    <>
                      {before}
                      <a href={url} onClick={url.startsWith('http://cheeks.uk/#') || url.startsWith('/#') ? (e) => { e.preventDefault(); const tab = url.split('#')[1]; if (['profile','inventory','creations'].includes(tab)) { switchTab(tab); } } : undefined} className="ml-1.5 bg-white/20 hover:bg-white/40 px-2 py-0.5 rounded-full text-[10px] transition-all border border-white/20 inline-flex items-center hover:scale-105 active:scale-95 font-semibold">
                        {label}
                      </a>
                      {after}
                    </>
                  );
                }
                return bannerText;
              })()}
            </span>
          </div>
        </div>
      )}

      {/* ── TOP NAV ── */}
      <div className="bg-slate-800/80 backdrop-blur-lg border-b border-slate-700/50 relative z-[100]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">

            {/* Left: live status + login */}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex flex-col items-stretch gap-1 w-[120px]">
                <a href="https://www.whatnot.com/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" className="group">
                </a>

                {!discordUser ? (
                  <div className="relative">
                    <button
                      onClick={handleDiscordLogin}
                      className="flex items-center justify-center space-x-1.5 text-white px-2.5 py-1 rounded-md transition-all duration-200 active:scale-95 w-full"
                      style={{background: 'linear-gradient(135deg, #22d3ee, #3b82f6)', boxShadow: '0 2px 8px rgba(34,211,238,0.25)'}}
                    >
                      <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="text-[11px] font-bold tracking-widest uppercase" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.1em'}}>Login</span>
                    </button>
                    {loginError && (
                      <div
                        className="absolute top-full left-0 mt-1.5 w-48 rounded-lg px-2.5 py-2 shadow-xl z-[999] transition-opacity duration-500"
                        style={{
                          backgroundColor: 'rgba(15,15,20,0.92)',
                          border: '1px solid rgba(255,102,102,0.4)',
                          opacity: loginErrorVisible ? 1 : 0
                        }}
                      >
                        <p className="text-[10px] leading-snug" style={{ color: '#FF6666' }}>You must be a member of PokéBubble to login.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative w-full" data-user-menu>
                    <button
                      onClick={() => setShowUserMenu(prev => !prev)}
                      className="flex items-center justify-center space-x-1.5 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 text-white px-2.5 py-1 rounded-md transition-all duration-200 w-full"
                    >
                      <img
                        src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                        alt="User"
                        className="h-4 w-4 rounded-full flex-shrink-0"
                      />
                      <span className="text-[11px] font-bold truncate max-w-[70px]">{discordUser.username}</span>
                      <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-400" />
                    </button>
                    {showUserMenu && (
                      <div className="absolute left-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[999] min-w-[120px]">
                        <div className="px-3 py-2 border-b border-slate-700">
                          <p className="text-white text-xs font-semibold truncate">{discordUser.username}</p>
                          <p className="text-slate-400 text-[10px]">Discord</p>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2 text-red-400 hover:bg-slate-700/50 text-xs font-medium transition-colors rounded-b-lg flex items-center space-x-2"
                        >
                          <LogIn className="h-3 w-3 rotate-180" />
                          <span>Log out</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Centre: logo */}
            <div className="flex justify-center items-center">
              <img src="/cheeks_logo.svg" alt="Cheeks Logo" className="h-12 object-contain filter drop-shadow-md opacity-90" />
            </div>

            {/* Right: social icons */}
            <div className="flex items-center justify-end space-x-1 flex-1">
              <a href="https://pokebubble.com/docs" className="group flex flex-col items-center w-10 relative">
                <div className="relative">
                  <img src="/discord.png" alt="Discord" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                  {voiceCount > 0 && (
                    <div className="absolute -top-1 -right-2 text-white px-1 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm" style={{backgroundColor:'#23a55a', border:'1px solid #1e293b'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                      <span className="font-medium leading-none" style={{ fontSize: '0.5rem' }}>{voiceCount}</span>
                    </div>
                  )}
                </div>
                {memberCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{formatCount(memberCount)}</span>
                  </div>
                )}
              </a>

              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center w-10">
                <img src="/instagram.png" alt="Instagram" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                {instagramCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{instagramCount}</span>
                  </div>
                )}
              </a>

              <button onClick={() => setShowVideoModal(true)} className="group flex flex-col items-center w-10 relative">
                <div className="relative">
                  <img src="/wnlogo.png" alt="Whatnot Logo" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                  {isLive && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-slate-800"></span>
                    </span>
                  )}
                </div>
                {whatnotCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{whatnotCount}</span>
                  </div>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ── TAB BAR ── */}
      <div className="bg-slate-900/95 backdrop-blur-lg sticky top-0 z-30 border-b border-slate-700/30">
        <div className="max-w-7xl mx-auto px-3">
          <div className="flex items-center justify-between py-1.5">

            {/* Pill tab group */}
            <div className="flex items-center gap-1">
              {[
                { id: 'profile', label: 'Profile', path: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></> },
                { id: 'inventory', label: 'Inventory', path: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></> },
                { id: 'creations', label: 'Creations', path: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/> },
              ].map(({ id, label, path }) => (
                <button
                  key={id}
                  onClick={() => switchTab(id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-200"
                  style={activeTab === id ? {
                    background: 'linear-gradient(135deg, #22d3ee, #3b82f6)',
                    color: 'white',
                    boxShadow: '0 2px 12px rgba(34,211,238,0.25)',
                  } : {
                    color: 'rgba(148,163,184,0.7)',
                    background: 'transparent',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    {path}
                  </svg>
                  {label}
                  {id === 'inventory' && images.length > 0 && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded-md" style={{background: activeTab === 'inventory' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)', color: activeTab === 'inventory' ? 'white' : 'rgba(148,163,184,0.5)'}}>
                      {images.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Right: Pokebubble watermark */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <a href="https://pokebubble.com" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.5rem', color: 'rgba(148,163,184,0.5)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', textDecoration: 'none' }}>Powered by</a>
              <a href="https://pokebubble.com" target="_blank" rel="noopener noreferrer">
                <img src="/pb_logo.png" alt="Pokebubble" style={{ height: '28px', width: 'auto', opacity: 1, display: 'block' }} />
              </a>
            </div>

          </div>
        </div>

        {/* Search bar — only shown in inventory, part of same sticky block */}
        {activeTab === 'inventory' && (
          <div className="bg-slate-900 border-b border-slate-700/50">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder={`Search… ${currentText}|`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 pr-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-slate-500 text-sm transition-all duration-200"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-2.5 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>

              {/* Category pills */}
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-0.5 scrollbar-hide">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3.5 py-1.5 rounded-xl font-medium transition-all duration-200 text-xs whitespace-nowrap flex-shrink-0 ${
                      selectedCategory === cat.id
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                        : 'bg-slate-700/50 text-slate-400 hover:text-white border border-slate-700/50'
                    }`}
                  >
                    {cat.name}
                    {cat.id !== 'all' && (
                      <span className="ml-1 opacity-70">({images.filter(img => img.category === cat.id).length})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          PROFILE TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'profile' && (
        <main className="flex-1">
          <style>{`
            @keyframes profileFadeUp {
              from { opacity: 0; transform: translateY(18px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .profile-panel {
              animation: profileFadeUp 0.45s ease both;
            }
          `}</style>
          <div className="profile-panel max-w-2xl mx-auto px-4 py-6 flex flex-col gap-4">

            {/* Avatar + name card */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">
              {/* Banner strip */}
              <div
                className="h-36 relative overflow-hidden"
                style={{ background: '#0c1a35' }}
              >
                <style>{`
                  @keyframes bannerShift {
                    0%   { background-position: 0% 50%; }
                    50%  { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                  }
                  .banner-anim {
                    position: absolute;
                    inset: 0;
                    background-image: url('https://rare-gallery.com/uploads/posts/1066121-illustration-anime-text-logo-cartoon-Pok-mon-Togepi-brand-ishmam-computer-wallpaper-font-organ.png');
                    background-size: cover;
                    background-position: center;
                  }
                  .banner-tint {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(160deg, rgba(12,26,53,0.72) 0%, rgba(10,58,92,0.65) 50%, rgba(13,42,74,0.72) 100%);
                  }
                  .banner-tint::after {
                    content: none;
                  }
                  @keyframes bannerBubbleFloat {
                    0%   { transform: translateY(0) translateX(0); opacity: 0; }
                    10%  { opacity: 0.7; }
                    80%  { opacity: 0.7; }
                    100% { transform: translateY(-200px) translateX(calc(8px * var(--bdrift))); opacity: 0; }
                  }
                  .banner-bubble {
                    position: absolute;
                    bottom: -20px;
                    border-radius: 50%;
                    background: radial-gradient(circle at 50% 50%,
                      rgba(255,255,255,0.04) 0%,
                      rgba(255,255,255,0.12) 60%,
                      rgba(255,255,255,0.35) 100%);
                    box-shadow:
                      inset 0 0 6px rgba(255,255,255,0.3),
                      inset 3px 0 6px rgba(180,100,255,0.25),
                      inset -3px 0 6px rgba(100,180,255,0.25),
                      inset 0 -3px 6px rgba(255,120,180,0.2);
                    mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    -webkit-mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    animation: bannerBubbleFloat linear infinite;
                    pointer-events: none;
                  }
                  .banner-bubble::after {
                    content: '';
                    position: absolute;
                    top: 18%;
                    left: 18%;
                    width: 22%;
                    height: 14%;
                    background: rgba(255,255,255,0.5);
                    border-radius: 50%;
                    transform: rotate(-35deg);
                    filter: blur(1px);
                  }
                `}</style>
                <div className="banner-anim" />
                <div className="banner-tint" />
                {/* Bubbles injected via ref */}
                <div ref={bannerBubbleRef} style={{position:'absolute',inset:0,pointerEvents:'none'}} />
                {/* Location + time */}
                <div style={{position:'absolute', top:'8px', right:'8px', display:'flex', alignItems:'center', pointerEvents:'none'}}>
                  <div style={{
                    display:'flex', alignItems:'center', gap:'4px',
                    background:'rgba(15,23,42,0.75)',
                    backdropFilter:'blur(6px)',
                    paddingLeft:'8px', paddingRight:'8px', paddingTop:'4px', paddingBottom:'4px',
                    borderRadius:'999px',
                    border:'1px solid rgba(51,65,85,0.5)',
                  }}>
                    <img src="https://flagcdn.com/gb.svg" alt="UK" style={{width:'12px',height:'12px',borderRadius:'50%',objectFit:'cover',flexShrink:0}} />
                    <span style={{color:'rgba(255,255,255,0.5)', fontSize:'0.55rem', fontWeight:500, letterSpacing:'0.02em', lineHeight:1, display:'block', paddingTop:'1px'}}>
                      London &nbsp;·&nbsp; {currentTime.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})} &nbsp;{currentTime.toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4 relative" style={{borderTop: '1px solid rgba(51,65,85,0.5)'}}>
                {/* Activity info — top-right, sits flush under the banner */}
                {myActivity && (
                  <div style={{position:'absolute', top:'0', right:'0', background:'rgb(30,41,59)', borderLeft:'1px solid rgba(51,65,85,0.5)', borderBottom:'1px solid rgba(51,65,85,0.5)', borderBottomLeftRadius:'12px', padding:'6px 10px', display:'flex', flexDirection:'column', gap:'1px', lineHeight:'1.2', whiteSpace:'nowrap', alignItems:'flex-end', zIndex:10}}>
                    <div className="text-slate-400 text-[9px] font-semibold uppercase tracking-wider">{myActivity.type}</div>
                    <div className="text-slate-400 text-[9px] font-medium">{myActivity.name}</div>
                    {myActivity.detail && <div className="text-slate-500 text-[9px]">{myActivity.detail}</div>}
                    {myActivity.state && !elapsed && <div className="text-slate-500 text-[9px]">{myActivity.state}</div>}
                    {elapsed && <div style={{display:'flex', alignItems:'center', gap:'2px', color:'#23a55a', fontSize:'0.6rem', fontWeight:500, lineHeight:1, marginTop:'2px'}}>
                      {myActivity.type === 'Watching' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>}
                      {myActivity.type === 'Playing' && <img src="/controller.svg" width="9" height="9" style={{flexShrink:0, filter:'brightness(0) saturate(100%) invert(52%) sepia(98%) saturate(346%) hue-rotate(95deg) brightness(93%) contrast(92%)'}} />}
                      {myActivity.type === 'Listening' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
                      {myActivity.type === 'Streaming' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>}
                      {!['Watching','Playing','Listening','Streaming'].includes(myActivity.type) && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>}
                      {elapsed}
                    </div>}
                  </div>
                )}
                {/* Avatar row */}
                <div className="flex items-start" style={{ marginTop: -36 }}>

                  {/* Avatar — left, relative so activity text can be absolute */}
                  <div className="relative flex-shrink-0">
                    {/* Floating hearts on load */}
                    <style>{`
                      @keyframes heartFloat {
                        0%   { transform: translateY(0) scale(0.6) rotate(var(--hr)); opacity: 0; }
                        15%  { opacity: 1; transform: translateY(-10px) scale(1) rotate(var(--hr)); }
                        80%  { opacity: 0.8; }
                        100% { transform: translateY(-70px) translateX(var(--hx)) scale(0.7) rotate(var(--hr)); opacity: 0; }
                      }
                      .pfp-heart {
                        position: absolute;
                        pointer-events: none;
                        animation: heartFloat 1.4s ease-out forwards;
                        font-size: 14px;
                        filter: drop-shadow(0 0 3px rgba(251,113,133,0.6));
                      }
                    `}</style>
                    {showHearts && [
                      { delay: '0s',    left: '30%', hx: '-8px',  hr: '-15deg', size: '13px' },
                      { delay: '0.15s', left: '55%', hx: '6px',   hr: '10deg',  size: '10px' },
                      { delay: '0.3s',  left: '20%', hx: '-4px',  hr: '-8deg',  size: '16px' },
                      { delay: '0.45s', left: '70%', hx: '10px',  hr: '20deg',  size: '11px' },
                      { delay: '0.6s',  left: '45%', hx: '-2px',  hr: '-5deg',  size: '14px' },
                      { delay: '0.1s',  left: '80%', hx: '5px',   hr: '12deg',  size: '9px'  },
                    ].map((h, i) => (
                      <span
                        key={i}
                        className="pfp-heart"
                        style={{
                          bottom: '10px',
                          left: h.left,
                          animationDelay: h.delay,
                          fontSize: h.size,
                          '--hx': h.hx,
                          '--hr': h.hr,
                        }}
                      >💗</span>
                    ))}
                    {/* Thought bubble */}
                    {myStatus && (
                      <div style={{position: 'absolute', top: '-8px', left: '75px', pointerEvents: 'none'}}>
                        <div style={{background: '#1e293b', border: '1px solid rgba(51,65,85,0.5)', borderRadius: '12px', padding: '4px 10px', whiteSpace: 'nowrap', marginLeft: '14px', lineHeight: 1, display: 'inline-flex', alignItems: 'center'}}>
                          <span style={{color: '#cbd5e1', fontSize: '0.6rem', fontWeight: 500, display: 'block'}}>{myStatus}</span>
                        </div>
                        <div style={{width: '8px', height: '8px', borderRadius: '50%', background: '#1e293b', border: '1px solid rgba(51,65,85,0.5)', marginTop: '3px', marginLeft: '8px'}}></div>
                      </div>
                    )}
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center shadow-xl" style={{border: '1px solid rgba(51,65,85,0.5)'}}>
                      <img src="/pfp.png" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    {/* Presence status dot */}
                    {(() => {
                      const s = myPresence || 'offline';
                      const dotColor = s === 'online' ? '#23a55a' : s === 'idle' ? '#f0b232' : s === 'dnd' ? '#f23f43' : '#80848e';
                      if (s === 'idle') return (
                        <svg
                          className="absolute bottom-0.5 right-0.5"
                          width="20" height="20" viewBox="0 0 20 20"
                          title="Idle"
                        >
                          <circle cx="10" cy="10" r="10" fill="#1e293b"/>
                          <circle cx="10" cy="10" r="6.5" fill="#f0b232"/>
                          <circle cx="7.5" cy="7.5" r="5" fill="#1e293b"/>
                        </svg>
                      );
                      return (
                        <div
                          className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: dotColor, border: '3.5px solid #1e293b' }}
                          title={s === 'dnd' ? 'Do Not Disturb' : s.charAt(0).toUpperCase() + s.slice(1)}
                        >
                          {s === 'dnd' && <div style={{width:'55%',height:'2px',background:'white',borderRadius:2}} />}
                        </div>
                      );
                    })()}
                  </div>



                  {/* Badge row — sits to the right of the avatar, aligned to its bottom */}
                  <div className="flex items-end gap-1.5 ml-3 pb-0.5" style={{marginTop: 'auto'}}>
                    {badges.map((badge) => (
                      <div
                        key={badge.key}
                        title={badge.label}
                        className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shadow-xl overflow-hidden"
                        style={{border: '1px solid rgba(51,65,85,0.5)'}}
                      >
                        <img src={badge.img} alt={badge.label} className="w-6 h-6 object-contain" />
                      </div>
                    ))}
                  </div>

                </div>

                {/* Name + bio */}
                <div className="mt-3">
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-white font-bold text-xl leading-tight">cheeks</h1>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
                      <defs>
                        <linearGradient id="checkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#22d3ee"/>
                          <stop offset="100%" stopColor="#3b82f6"/>
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="10" fill="url(#checkGrad)"/>
                      <path d="M7.5 12.5l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className="text-xs font-medium mt-0.5 mb-2" style={{ background: 'linear-gradient(135deg, #22d3ee, #3b82f6)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>@togecheeks</p>
                  <p className="text-slate-300 leading-relaxed" style={{maxWidth: 'calc(100% - 80px)', fontSize: 'clamp(0.6rem, 2.5vw, 0.75rem)', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
                    Everything Pokémon — cards, collectibles & more ✨
                  </p>
                </div>

                {/* Corner inventory button — bottom right of card content */}
                <button
                  onClick={() => switchTab('inventory')}
                  className="absolute bottom-0 right-0 flex flex-col items-center justify-center gap-0.5 border-t border-l border-cyan-400/30 rounded-tl-xl px-3 py-2 transition-all duration-200 active:scale-95"
                  style={{background: 'linear-gradient(135deg, #22d3ee, #3b82f6)'}}
                >
                  <Package className="h-4 w-4 text-white" />
                  <span className="text-white font-medium" style={{fontSize:'0.55rem', lineHeight:1}}>Browse Inventory</span>
                </button>

              </div>
            </div>

            {/* Social Links */}
            <div className="flex flex-col gap-2.5">

              {/* Discord */}
              <a href="https://pokebubble.com/docs" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                  <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#5865F2'}} />
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <div className="relative">
                      <img src="/discord.png" alt="Discord" className="w-6 h-6 object-contain" />
                      {voiceCount > 0 && (
                        <div className="absolute -top-1 -right-2 text-white px-1 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm" style={{backgroundColor:'#23a55a', border:'1px solid #1e293b'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                          <span className="font-medium leading-none" style={{fontSize:'0.5rem'}}>{voiceCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>Discord</div>
                  <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                    <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                      <Users className="h-2 w-2 flex-shrink-0" />
                      {memberCount !== null ? formatCount(memberCount) : '—'}
                    </div>
                    {voiceCount > 0 ? (
                      <div className="flex items-center gap-0.5 font-medium" style={{fontSize:'0.6rem', lineHeight:1, color:'#23a55a'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="8" height="8"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        {voiceCount} in voice
                      </div>
                    ) : (
                      <div className="flex items-center gap-0.5 font-medium" style={{fontSize:'0.6rem', lineHeight:1, color:'#64748b'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="8" height="8"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        0 in voice
                      </div>
                    )}
                    <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>discord.cheeks.uk</span>
                  </div>
                </a>

              {/* Instagram */}
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#E1306C'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/instagram.png" alt="Instagram" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>Instagram</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <Users className="h-2 w-2 flex-shrink-0" />
                    {instagramCount !== null ? formatCount(instagramCount) : '—'}
                  </div>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>@togecheeks</span>
                </div>
              </a>

              {/* Whatnot */}
              <button onClick={() => setShowVideoModal(true)} className="relative w-full flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#FADC00'}} />
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <div className="relative">
                    <img src="/wnlogo.png" alt="Whatnot" className="w-6 h-6 object-contain" />
                    {isLive && (
                      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-slate-800"></span></span>
                    )}
                  </div>
                </div>
                <div className="text-white font-semibold leading-none text-left uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>Whatnot</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <Users className="h-2 w-2 flex-shrink-0" />
                    {whatnotCount !== null ? formatCount(whatnotCount) : '—'}
                  </div>
                  {isLive ? (
                    <div className="flex items-center gap-1 text-red-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                      <span className="relative flex h-1.5 w-1.5 flex-shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span></span>
                      LIVE NOW
                    </div>
                  ) : (
                    <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Offline</span>
                  )}
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>@togecheeks</span>
                </div>
              </button>

              {/* MakerWorld */}
              <a href="https://makerworld.com/en/@togecheeks" target="_blank" rel="noopener noreferrer" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#1DB954'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                    <img src="/makerworld.png" alt="MakerWorld" className="w-5 h-5 object-contain" />
                  </div>
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>MakerWorld</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Lv. 18</span>
                  <span className="text-slate-500 font-medium flex items-center gap-0.5" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66-.23-.45-.52-.86-.88-1.22L14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83V19c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.12z"/></svg>
                    3k
                  </span>
                  <span className="text-slate-500 font-medium flex items-center gap-0.5" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zm-14 9v2h14v-2H5z"/></svg>
                    2.6k
                  </span>
                </div>
              </a>

              {/* Pokémon GO */}
              <div className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 cursor-pointer active:scale-[0.98] transition-all duration-150" onClick={() => { navigator.clipboard?.writeText('664463929998'); setShowPogoModal(true); setShowCopiedToast(true); setTimeout(() => setShowCopiedToast(false), 2500); }}>
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#030CA6'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/pogo.png" alt="Pokémon GO" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>Pokémon GO</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Lv. 70</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Valor</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>togecheeks</span>
                </div>
              </div>

              {/* Steam */}
              <a href="https://steamcommunity.com/id/togecheeks/" target="_blank" rel="noopener noreferrer" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#0B79AC'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/steam.png" alt="Steam" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.65rem] md:text-[0.82rem]" style={{fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", letterSpacing:'0.12em'}}>Steam</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>cheeks</span>
                </div>
              </a>

            </div>

            {/* Hunting + New Product row */}
            <div className="grid grid-cols-2 gap-3 items-start">

              {/* Currently Hunting card */}
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col">
                {/* Header — fixed height */}
                <div className="flex items-center gap-1.5 px-3 py-2 h-8">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <span className="text-slate-400 text-[10px] font-semibold uppercase" style={{letterSpacing:'0.12em', fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif"}}>Hunting</span>
                </div>
                {/* Image — fixed height */}
                <div className="relative mx-3 rounded-xl bg-slate-900/80 border border-slate-700/50 overflow-hidden flex-shrink-0" style={{height:'110px'}}>
                  <div className="absolute inset-0" style={{background:'radial-gradient(ellipse at 50% 30%, rgba(99,102,241,0.15) 0%, transparent 70%)'}} />
                  <img src="/gbmini.png" alt="Hunting" className="absolute inset-0 w-full h-full object-contain p-2" onError={(e) => { e.target.style.display='none'; }} />
                </div>
                {/* Info */}
                <div className="px-3 pt-2 pb-2 flex-1">
                  <p className="text-white text-[11px] font-semibold leading-tight">Togepi Holo Prism</p>
                  <p className="text-slate-500 text-[9px] font-medium leading-snug mt-0.5">GB Pocket · N64 Stadium</p>
                </div>
                {/* Bottom strip — same height as New card's strip */}
                <div className="border-t border-slate-700/40 px-3 py-2 flex items-center gap-1.5 h-9">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="#f472b6" className="flex-shrink-0"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                  <span className="text-slate-500 text-[9px] font-medium uppercase" style={{letterSpacing:'0.08em'}}>Faves</span>
                  <div className="flex gap-1 ml-1">
                    <div className="w-5 h-5 rounded-md bg-slate-900/80 border border-slate-700/50 flex items-center justify-center overflow-hidden">
                      <img src="/togepi.png" alt="Togepi" className="w-full h-full object-contain p-0.5" onError={(e) => { e.target.style.display='none'; }} />
                    </div>
                    <div className="w-5 h-5 rounded-md bg-slate-900/80 border border-slate-700/50 flex items-center justify-center overflow-hidden">
                      <img src="/lugia.png" alt="Lugia" className="w-full h-full object-contain p-0.5" onError={(e) => { e.target.style.display='none'; }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* New Product card */}
              <button onClick={() => { switchTab('creations'); setTimeout(() => { document.getElementById('digital-stand-card')?.scrollIntoView({behavior:'smooth', block:'start'}); }, 120); }} className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden active:scale-[0.98] transition-all duration-150 text-left w-full flex flex-col">
                {/* Header — fixed height, matches Hunting */}
                <div className="flex items-center gap-1.5 px-3 py-2 h-8">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span className="text-[10px] font-bold uppercase" style={{letterSpacing:'0.12em', fontFamily:"'Inter', 'Barlow Condensed', 'Arial Narrow', sans-serif", background:'linear-gradient(135deg, #22d3ee, #3b82f6)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent'}}>New</span>
                </div>
                {/* Image — fixed height, matches Hunting */}
                <div className="relative mx-3 rounded-xl overflow-hidden border border-slate-700/50 bg-slate-900/80 flex-shrink-0" style={{height:'110px'}}>
                  <div className="absolute inset-0" style={{background:'radial-gradient(ellipse at 50% 50%, rgba(34,211,238,0.18) 0%, rgba(59,130,246,0.08) 50%, transparent 75%)'}} />
                  <img src="/digital_stand.png" alt="Digital Display Stand" className="absolute inset-0 w-full h-full object-contain p-2" style={{zIndex:1}} onError={(e) => { e.target.style.display='none'; }} />
                </div>
                {/* Info */}
                <div className="px-3 pt-2 pb-2 flex-1">
                  <p className="text-white text-[11px] font-semibold leading-tight">Digital Display Stand <span className="text-[9px] font-bold text-cyan-400">V2</span></p>
                  <p className="text-slate-500 text-[9px] font-medium leading-snug mt-0.5">Tap to explore</p>
                </div>
                {/* Bottom strip — same height as Hunting's strip */}
                <div className="border-t border-slate-700/40 px-3 py-2 flex items-center justify-between h-9">
                  <span className="text-slate-500 text-[9px] font-medium">Available now</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-slate-500 font-semibold uppercase" style={{fontSize:'0.55rem', letterSpacing:'0.08em'}}>From</span>
                    <span className="text-white text-[10px] font-bold">£79.99</span>
                  </div>
                </div>
              </button>

            </div>

            {/* Fun Fact Panel */}
            {funFact && (
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-3 flex items-start gap-3">
                <img src="/pball.svg" alt="" className="h-4 w-4 flex-shrink-0 mt-0.5" style={{filter:'brightness(0) saturate(100%) invert(43%) sepia(8%) saturate(869%) hue-rotate(175deg) brightness(95%) contrast(87%)'}} />
                <p className="text-slate-500 leading-relaxed" style={{fontSize:'0.7rem'}}>{funFact}</p>
              </div>
            )}

          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          INVENTORY TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'inventory' && (
        <main className="flex-1">
          <style>{`
            @keyframes inventoryFadeUp {
              from { opacity: 0; transform: translateY(14px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .inv-card {
              animation: inventoryFadeUp 0.35s ease both;
            }
          `}</style>

          {/* Grid */}
          <div className="max-w-7xl mx-auto px-3 py-4">

            {/* Inventory updating bar */}
            <div className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/40 rounded-2xl px-4 py-2 mb-4">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                <img src="/pball.svg" alt="" className="h-4 w-4 animate-spin" style={{animationDuration:"2s", filter:"brightness(0) invert(1)"}} />
              </div>
              <div className="w-px h-6 bg-slate-700/60 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-xs font-semibold leading-none">Inventory Updating...</p>
                <p className="text-slate-500 text-[10px] mt-0.5 leading-none">New items are being added to this page shortly.</p>
              </div>
            </div>

            {filteredImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredImages.map((image, idx) => (
                  <div
                    key={image.id}
                    className="inv-card group bg-slate-800/90 backdrop-blur-sm rounded-xl border border-slate-700/50 hover:border-cyan-400/50 active:border-cyan-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-400/10 overflow-hidden cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.25)] hover:-translate-y-0.5"
                    style={{animationDelay:`${Math.min(idx * 0.03, 0.6)}s`}}
                    onClick={() => setSelectedInventoryImage(image)}
                  >
                    <div className="aspect-square bg-slate-700/50 flex items-center justify-center relative overflow-hidden">
                      <img
                        src={`/${image.folder}/${image.filename}`}
                        alt={image.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 flex items-center justify-center" style={{ display: 'none' }}>
                        <div className="text-center p-4">
                          <div className="w-16 h-16 mx-auto mb-2 opacity-30">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-500">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2"/>
                              <polyline points="21 15 16 10 5 21" strokeWidth="2"/>
                            </svg>
                          </div>
                          <p className="text-xs text-slate-500 font-medium">Image not found</p>
                        </div>
                      </div>
                      {/* Bottom gradient overlay */}
                      <div className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none" style={{background:'linear-gradient(to top, rgba(15,23,42,0.7), transparent)'}}/>
                      {image.grade && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-2 py-1 rounded-md text-xs font-bold shadow-lg backdrop-blur-sm border border-cyan-400/50">
                          {image.grade}
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors leading-tight">
                        {image.name}
                      </h3>
                      {image.set.split('  ').map((line, idx) => (
                        <p key={idx} className="text-[10px] text-slate-400 leading-tight">{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-12 text-center">
                <p className="text-slate-400">No items found matching your search.</p>
              </div>
            )}
          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          CREATIONS TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'creations' && (
        <main className="flex-1">
          <style>{`
            @keyframes creationFadeUp {
              from { opacity: 0; transform: translateY(18px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes bellRing {
              0%,100% { transform: rotate(0deg); }
              10%     { transform: rotate(14deg); }
              20%     { transform: rotate(-12deg); }
              30%     { transform: rotate(10deg); }
              40%     { transform: rotate(-8deg); }
              50%     { transform: rotate(0deg); }
            }
            .creation-card {
              animation: creationFadeUp 0.45s ease both;
            }
            .instructions-body {
              display: grid;
              grid-template-rows: 0fr;
              transition: grid-template-rows 0.35s ease;
            }
            .instructions-body.open {
              grid-template-rows: 1fr;
            }
            .instructions-inner {
              overflow: hidden;
            }
            @keyframes shimmer {
              0%   { background-position: -200% center; }
              100% { background-position: 200% center; }
            }
            .instructions-shimmer {
              background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 55%, transparent 100%);
              background-size: 200% auto;
              animation: shimmer 4s ease-in-out infinite;
            }
            .epaper-img {
              filter: grayscale(100%) contrast(1.4) brightness(1.1);
              image-rendering: pixelated;
            }
          `}</style>
          <svg width="0" height="0" style={{position:'absolute'}}>
            <defs>
              <filter id="epaper">
                <feColorMatrix type="saturate" values="0"/>
                <feComponentTransfer>
                  <feFuncR type="discrete" tableValues="0 0.42 0.78 1"/>
                  <feFuncG type="discrete" tableValues="0 0.42 0.78 1"/>
                  <feFuncB type="discrete" tableValues="0 0.42 0.78 1"/>
                </feComponentTransfer>
              </filter>
            </defs>
          </svg>

          <div className="max-w-2xl mx-auto px-4 py-6">
            <div className="flex flex-col gap-5">

            {/* ── CREATION CARD V3 ── */}
            <div className="creation-card bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">
              {/* Product image — placeholder */}
              <div className="relative w-full overflow-hidden flex items-center justify-center" style={{aspectRatio:'4/3', background:'radial-gradient(ellipse at 60% 50%, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.06) 40%, rgba(15,23,42,0.0) 70%)'}}>
                <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(59,130,246,0.06), transparent)', zIndex:0}} />
                {/* Centre placeholder — spinning pokeball + text */}
                <div className="flex flex-col items-center justify-center gap-3" style={{position:'relative', zIndex:2}}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                    <img src="/pball.svg" alt="" className="h-6 w-6 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) invert(1)'}} />
                  </div>
                  <span className="text-slate-400 font-semibold tracking-wide" style={{fontSize:'0.85rem'}}>Image Coming Soon</span>
                </div>
                {/* Full Colour pill — top right */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #22d3ee, #3b82f6)', zIndex:3}}>
                  Full Colour
                </div>
                {/* V3 version badge — bottom left */}
                <div className="absolute bottom-3 left-3 flex items-center justify-center rounded-full text-white font-bold text-[11px]" style={{width:'28px', height:'28px', background:'linear-gradient(135deg,#22d3ee,#3b82f6)', zIndex:3, boxShadow:'0 0 8px rgba(34,211,238,0.4)'}}>
                  V3
                </div>
                {/* Mini preview placeholder — top left (600×400 ratio, taller than V2) */}
                <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5" style={{zIndex:3}}>
                  <div className="rounded-lg overflow-hidden border border-slate-600/50 flex items-center justify-center" style={{width:'99px', height:'66px', background:'#0f172a'}}>
                    <div className="flex flex-col items-center gap-1 opacity-40">
                      <img src="/pball.svg" alt="" className="h-5 w-5 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(400%) hue-rotate(160deg)'}} />
                    </div>
                  </div>
                  <div className="w-full text-center px-2 py-0.5 rounded-full text-[9px] font-semibold text-white/70" style={{background:'rgba(0,0,0,0.75)'}}>
                    Example content
                  </div>
                </div>
                {/* Thumbnail placeholders — bottom right */}
                <div className="absolute bottom-3 right-3 flex items-center gap-2" style={{zIndex:3}}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="rounded-lg overflow-hidden flex items-center justify-center"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: '#0f172a',
                        border: '2px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <img src="/pball.svg" alt="" className="h-5 w-5 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(400%) hue-rotate(160deg)', opacity:0.35}} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Info block */}
              <div className="px-4 pt-4 pb-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Digital Display Card &amp; Slab Stand V3</h2>
                  <div className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                    <span className="text-white font-semibold uppercase tracking-widest" style={{fontSize:'0.55rem'}}>From</span>
                    <span className="text-white font-bold text-base leading-tight">£149.00</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    'Full Colour',
                    'Wireless Control',
                    'Low Power',
                    'Stream Deck Ready',
                    'Easy Setup',
                    'No App Required',
                    'Plug & Play',
                    'Always-On Display',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="text-slate-300 font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">A wirelessly controlled display stand with a full colour screen, designed for graded slabs and raw cards from all major TCGs — Pokémon, MTG, Yu-Gi-Oh!, Disney, Sports and more. Change what's shown on screen from any device on your Wi-Fi: custom graphics, shout-outs, offers, discount codes, or stream overlays. Stream Deck compatible. The screen holds its image without power — and is only needed when updating the display.</p>
              </div>

              {/* Divider + Setup Instructions (non-functional) */}
              <div className="border-t border-slate-700/50" />
              <div className="px-4 py-2">
                <div className="w-full flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 self-start mt-0.5" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                    </div>
                    <div className="w-px h-6 bg-slate-700/60 flex-shrink-0 self-start mt-0.5" />
                    <div className="text-left">
                      <span className="text-xs font-semibold text-slate-300">Setup Instructions</span>
                      <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Follow these steps to get your device connected and ready to use.</p>
                    </div>
                  </div>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 self-start mt-0.5" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-700/50" />

              {/* Update Firmware (non-functional) */}
              <div className="px-4 py-2">
                <div className="w-full flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                      </svg>
                    </div>
                    <div className="w-px h-6 bg-slate-700/60 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-semibold text-slate-300">Update Firmware</span>
                      <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Keep your device up to date with the latest features.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="font-mono" style={{fontSize:'0.55rem', color:'#4ade80'}}>Latest: v1.2.0</span>
                    </div>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-700/50" />

            </div>

            {/* ── CREATION CARD ── */}
            {[{
              id: 1,
              name: 'Digital Display Card & Slab Stand V2',
              tag: '3D Printed',
              badge: 'Digital + Physical',
              description: 'A wirelessly controlled display stand, designed for graded slabs and raw cards from all major TCGs — Pokémon, MTG, Yu-Gi-Oh!, Disney, Sports and more. Change what\'s shown on screen from any device on your Wi-Fi: custom graphics, shout-outs, offers, discount codes, or stream overlays. Stream Deck compatible. The screen holds its image without power — and is only needed when updating the display.',
              price: '£79.99',
              availability: 'Available',
              steps: [
                {
                  title: 'Power On',
                  detail: 'Unbox your stand and plug it into a power source using the provided USB cable. The device will boot up automatically within seconds.'
                },
                {
                  title: 'Connect to Display Network',
                  detail: <span>On your smartphone or computer, open your Wi-Fi settings and connect to the network named: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:'3px'}}><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg><strong className="text-cyan-400">Digital Display</strong></span>
                },
                {
                  title: 'Configure Your Device',
                  detail: <span>Once connected, a setup page should automatically open — if it doesn't, navigate to <code className="px-1.5 py-0.5 rounded text-cyan-400 font-mono text-[11px]" style={{background:'rgba(34,211,238,0.1)', border:'1px solid rgba(34,211,238,0.2)'}}>192.168.4.1</code> in your browser. Click the Scan WiFi button and select the network you want to use, enter your WiFi password and a device name (e.g. "My Stream Stand"), then tap Complete & Restart.</span>,
                },
                {
                  title: 'Complete Setup',
                  detail: 'The device will reboot and connect to your network. A "Success" message will appear on the screen along with the control panels IP Address (e.g. 192.168.1.50) — note this down or bookmark it.'
                },
                {
                  title: 'Access Control Panel',
                  detail: 'Type the control panel IP Address into a web browser on any device connected to the same WiFi network. From here you can manage and push content to your display!'
                },
              ]
            }].map((item) => (
              <div key={item.id} id="digital-stand-card" className="creation-card bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">

                {/* Product image */}
                <div className="relative w-full overflow-hidden" style={{aspectRatio:'4/3', background:'radial-gradient(ellipse at 60% 50%, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.06) 40%, rgba(15,23,42,0.0) 70%)'}}>
                  {/* Subtle grid lines */}
                  <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                  {/* Corner glow */}
                  <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(59,130,246,0.06), transparent)', zIndex:0}} />
                  {/* Pulsing energy orb behind stand */}
                  <style>{`
                    @keyframes orbPulse {
                      0%,100% { transform: scale(1); opacity: 0.18; }
                      50%     { transform: scale(1.15); opacity: 0.32; }
                    }
                    @keyframes orbPulse2 {
                      0%,100% { transform: scale(1.1); opacity: 0.10; }
                      50%     { transform: scale(0.95); opacity: 0.22; }
                    }
                    .orb1 { animation: orbPulse 3s ease-in-out infinite; transform-origin: center; }
                    .orb2 { animation: orbPulse2 4s ease-in-out infinite; transform-origin: center; }
                  `}</style>
                  <div className="orb1 absolute inset-0 rounded-full" style={{
                    zIndex: 1,
                    background: 'radial-gradient(ellipse at 50% 60%, #22d3ee 0%, #3b82f6 35%, transparent 70%)',
                    filter: 'blur(40px)',
                  }}/>
                  <div className="orb2 absolute inset-0 rounded-full" style={{
                    zIndex: 1,
                    background: 'radial-gradient(ellipse at 50% 55%, #ffffff 0%, #22d3ee 20%, transparent 65%)',
                    filter: 'blur(30px)',
                  }}/>
                  <img
                    src={selectedStandImage}
                    alt={item.name}
                    className="w-full h-full object-contain px-8"
                    style={{position:'relative', zIndex:2}}
                  />
                  {/* New Improved Design pill — top right */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #22d3ee, #3b82f6)'}}>
                    New Improved Design
                  </div>
                  {/* Mini cycling display — top left */}
                  <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                    <div className="relative rounded overflow-hidden" style={{width:'99px', height:'43px', background:'#0f172a'}}>
                      {[1,2,3,4,5].map((n) => (
                        <img
                          key={n}
                          src={`/display${n}.png`}
                          alt={`Display example ${n}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{
                            opacity: displayImageIndex === n - 1 ? 1 : 0,
                            filter: 'url(#epaper)',
                            transition: 'opacity 0.7s ease',
                          }}
                        />
                      ))}
                    </div>
                    <div className="w-full text-center px-2 py-0.5 rounded-full text-[9px] font-semibold text-white/70" style={{background:'rgba(0,0,0,0.75)'}}>
                      Example content
                    </div>
                  </div>
                  {/* V2 version badge — bottom left */}
                  <div className="absolute bottom-3 left-3 flex items-center justify-center rounded-full text-white font-bold text-[11px]" style={{width:'28px', height:'28px', background:'linear-gradient(135deg,#22d3ee,#3b82f6)', boxShadow:'0 0 8px rgba(34,211,238,0.4)', zIndex:3}}>
                    V2
                  </div>
                  {/* Thumbnail gallery — bottom right */}
                  <div className="absolute bottom-3 right-3 flex items-center gap-2" style={{zIndex:3}}>
                    {['/digital_stand.png', '/digital_stand_rear.png', '/digital_stand_side.png'].map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedStandImage(src)}
                        className="rounded-lg overflow-hidden transition-all duration-200"
                        style={{
                          width: '44px',
                          height: '44px',
                          background: '#0f172a',
                          border: selectedStandImage === src ? '2px solid #22d3ee' : '2px solid rgba(255,255,255,0.15)',
                          boxShadow: selectedStandImage === src ? '0 0 8px rgba(34,211,238,0.4)' : 'none',
                        }}
                      >
                        <img src={src} alt={`View ${i + 1}`} className="w-full h-full object-contain p-1" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info block */}
                <div className="px-4 pt-4 pb-3">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <h2 className="text-white font-bold text-base leading-tight">{item.name}</h2>
                    <div className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                      <span className="text-white font-semibold uppercase tracking-widest" style={{fontSize:'0.55rem'}}>From</span>
                      <span className="text-white font-bold text-base leading-tight">{item.price}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      '4 Colour Greyscale',
                      'Wireless Control',
                      'Low Power',
                      'Stream Deck Ready',
                      'Easy Setup',
                      'No App Required',
                      'Plug & Play',
                      'Always-On Display',
                    ].map((point) => (
                      <div key={point} className="flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <span className="text-slate-300 font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed">{item.description}</p>
                </div>

                {/* Divider + Instructions accordion with shimmer */}
                <div className="border-t border-slate-700/50" />
                <div className="px-4 py-2">
                  <button
                    onClick={() => { setExpandedInstructions(expandedInstructions === item.id ? null : item.id); setExpandedFirmware(null); }}
                    className="w-full flex items-start justify-between group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 self-start mt-0.5" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                        </svg>
                      </div>
                      <div className="w-px h-6 bg-slate-700/60 flex-shrink-0 self-start mt-0.5" />
                      <div className="text-left">
                        <span className="text-xs font-semibold text-slate-300 group-hover:text-white transition-colors">Setup Instructions</span>
                        <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Follow these steps to get your device connected and ready to use.</p>
                      </div>
                    </div>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 self-start mt-0.5 transition-transform duration-300" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)', transform: expandedInstructions === item.id ? 'rotate(90deg)' : 'rotate(0deg)'}}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </button>
                </div>
                <div className={`border-t border-slate-700/50${expandedInstructions === item.id ? '' : ''}`} />

                <div className={`instructions-body${expandedInstructions === item.id ? ' open' : ''}`}>
                  <div className="instructions-inner px-4 pb-3">
                      <div className="mt-3 flex flex-col rounded-xl overflow-hidden border border-slate-700/40">
                        {item.steps.map((step, i) => (
                          <div key={i} className={`flex flex-col px-3 py-3 ${i < item.steps.length - 1 ? 'border-b border-slate-700/40' : ''} ${i % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-800/20'}`}>
                            <div className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-md flex items-center justify-center text-[9px] font-bold text-slate-400 bg-slate-700/60 border border-slate-600/50">
                                {i + 1}
                              </span>
                              <div>
                                <p className="text-white text-xs font-semibold mb-0.5">{step.title}</p>
                                <p className="text-slate-400 text-xs leading-relaxed">{step.detail}</p>
                              </div>
                            </div>
                            {/* Step mockup */}
                            {i === 0 && (
                              <div className="mt-3 mx-1 rounded-lg overflow-hidden border border-slate-700/40">
                                <svg width="100%" viewBox="0 0 680 160" xmlns="http://www.w3.org/2000/svg">
                                  <style>{`
                                    @keyframes plugIn { 0%{transform:translateX(60px);} 60%{transform:translateX(0px);} 80%{transform:translateX(-3px);} 100%{transform:translateX(0px);} }
                                    @keyframes flowDash { 0%{stroke-dashoffset:24} 100%{stroke-dashoffset:0} }
                                    @keyframes fadeFlow { 0%,50%{opacity:0} 70%,100%{opacity:1} }
                                    .plug-group { animation: plugIn 2s cubic-bezier(0.4,0,0.2,1) infinite; transform-origin: 172px 80px; }
                                    .flow { animation: flowDash 0.5s linear infinite, fadeFlow 2s ease infinite; }
                                  `}</style>
                                  <rect x="0" y="0" width="680" height="160" fill="#0f172a" rx="10"/>
                                  <text x="80" y="80" textAnchor="middle" fill="#22d3ee" fontSize="16" fontFamily="monospace" fontWeight="700" letterSpacing="2" transform="rotate(-90, 80, 80)">USB</text>
                                  <rect x="100" y="40" width="22" height="80" rx="4" fill="#1e293b" stroke="#334155" strokeWidth="1.5"/>
                                  <rect x="107" y="66" width="10" height="28" rx="2" fill="#0f172a" stroke="#475569" strokeWidth="1"/>
                                  <rect x="109" y="70" width="6" height="6" rx="1" fill="#334155"/>
                                  <rect x="109" y="80" width="6" height="6" rx="1" fill="#334155"/>
                                  <g className="plug-group">
                                    <rect x="122" y="66" width="42" height="28" rx="4" fill="#22d3ee"/>
                                    <rect x="126" y="70" width="34" height="20" rx="2" fill="#0e7490"/>
                                    <rect x="131" y="74" width="6" height="5" rx="1" fill="#22d3ee" opacity="0.6"/>
                                    <rect x="141" y="74" width="6" height="5" rx="1" fill="#22d3ee" opacity="0.6"/>
                                    <rect x="131" y="82" width="6" height="4" rx="1" fill="#22d3ee" opacity="0.4"/>
                                    <rect x="141" y="82" width="6" height="4" rx="1" fill="#22d3ee" opacity="0.4"/>
                                    <rect x="164" y="72" width="8" height="16" rx="1" fill="#22d3ee"/>
                                    <rect x="172" y="58" width="64" height="44" rx="6" fill="#1e293b" stroke="#334155" strokeWidth="1.5"/>
                                    <rect x="176" y="70" width="56" height="20" rx="2" fill="#0f172a"/>
                                    <rect x="180" y="74" width="48" height="12" rx="2" fill="#111827"/>
                                    <path d="M236 80 C262 80 272 60 302 65 C332 70 342 90 372 88 C402 86 412 72 442 75 C472 78 482 88 512 85 C542 82 562 72 600 75 C630 78 650 82 680 78" stroke="#334155" strokeWidth="10" strokeLinecap="round" fill="none"/>
                                    <path d="M236 80 C262 80 272 60 302 65 C332 70 342 90 372 88 C402 86 412 72 442 75 C472 78 482 88 512 85 C542 82 562 72 600 75 C630 78 650 82 680 78" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" fill="none" strokeDasharray="8 8" className="flow"/>
                                  </g>
                                </svg>
                              </div>
                            )}
                            {i === 1 && (
                              <div className="mt-3 mx-1 rounded-lg overflow-hidden border border-slate-700/40 bg-slate-900/60 p-3 flex items-center gap-3">
                                {/* Phone */}
                                <div className="flex-shrink-0 rounded-xl overflow-hidden flex flex-col" style={{width:'72px',height:'120px',background:'#1a1a2e',border:'2px solid #444'}}>
                                  <div style={{height:'10px',background:'#111',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                    <div style={{width:'5px',height:'5px',borderRadius:'50%',background:'#333'}}/>
                                  </div>
                                  <div style={{flex:1,background:'#0f0f18',padding:'6px 5px',display:'flex',flexDirection:'column',gap:'3px'}}>
                                    <div style={{fontSize:'8px',color:'#666',textAlign:'center',marginBottom:'3px'}}>Wi-Fi</div>
                                    <div style={{display:'flex',alignItems:'center',gap:'3px',padding:'3px 4px',borderRadius:'3px',background:'#00d4ff18',border:'0.5px solid #00d4ff44'}}>
                                      <span style={{fontSize:'7px',color:'#00d4ff',flex:1}}>Digital Display</span>
                                      <div style={{width:'7px',height:'7px',borderRadius:'50%',background:'#00d4ff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'5px',color:'#000'}}>✓</div>
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:'3px',padding:'3px 4px',borderRadius:'3px',background:'#ffffff08'}}>
                                      <span style={{fontSize:'7px',color:'#888',flex:1}}>Virgin Media 5G</span>
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:'3px',padding:'3px 4px',borderRadius:'3px',background:'#ffffff08'}}>
                                      <span style={{fontSize:'7px',color:'#888',flex:1}}>BT-Hub-5432</span>
                                    </div>
                                  </div>
                                </div>
                                {/* Arrow + callout */}
                                <div className="text-slate-600 text-base flex-shrink-0">→</div>
                                <div style={{flex:1,background:'#00d4ff12',border:'1px solid #00d4ff33',borderRadius:'8px',padding:'8px 10px'}}>
                                  <div style={{fontSize:'9px',color:'#00d4ff88',marginBottom:'4px'}}>Connect to:</div>
                                  <div style={{fontSize:'11px',color:'#22d3ee',fontFamily:'monospace',fontWeight:600}}>Digital Display</div>
                                  <div style={{fontSize:'9px',color:'#ffffff33',marginTop:'4px'}}>in Wi-Fi settings</div>
                                </div>
                              </div>
                            )}
                            {i === 2 && (
                              <div className="mt-3 mx-1 rounded-lg overflow-hidden border border-slate-700/40" style={{background:'#0f0f13'}}>
                                <div style={{background:'linear-gradient(135deg,#1a1a2e,#0f3460)',borderBottom:'1px solid #00d4ff22',padding:'7px 10px',display:'flex',alignItems:'center',gap:'8px'}}>
                                  <span style={{fontSize:'10px',fontWeight:500,color:'#00d4ff',letterSpacing:'0.5px'}}>Digital Display — Setup</span>
                                  <div style={{marginLeft:'auto',background:'#ffffff0d',border:'1px solid #00d4ff22',borderRadius:'3px',color:'#00d4ff',fontSize:'9px',padding:'2px 6px',fontFamily:'monospace'}}>192.168.4.1</div>
                                </div>
                                <div style={{display:'flex',gap:'10px',padding:'10px'}}>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:'9px',color:'#00d4ffaa',marginBottom:'5px'}}>Available networks</div>
                                    {['Virgin Media 5G','BT-Hub-5432 ✓','Sky-Hub-2A1B'].map((n,idx) => (
                                      <div key={idx} style={{display:'flex',alignItems:'center',gap:'5px',padding:'4px 6px',borderRadius:'4px',border:`0.5px solid ${idx===1?'#00d4ff66':'#ffffff12'}`,background:idx===1?'#00d4ff12':'#1e1e2e',marginBottom:'3px'}}>
                                        <span style={{fontSize:'9px',color:idx===1?'#00d4ff':'#888',flex:1}}>{n}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{flex:1,display:'flex',flexDirection:'column',gap:'6px'}}>
                                    <div>
                                      <div style={{fontSize:'8px',color:'#00d4ffaa',marginBottom:'2px'}}>Wi-Fi password</div>
                                      <div style={{background:'#1e1e2e',border:'1px solid #00d4ff33',borderRadius:'3px',color:'#888',padding:'3px 6px',fontSize:'9px'}}>••••••••••</div>
                                    </div>
                                    <div>
                                      <div style={{fontSize:'8px',color:'#00d4ffaa',marginBottom:'2px'}}>Device name</div>
                                      <div style={{background:'#1e1e2e',border:'1px solid #00d4ff66',borderRadius:'3px',color:'#22d3ee',padding:'3px 6px',fontSize:'9px',fontFamily:'monospace'}}>My Stream Stand</div>
                                    </div>
                                    <div style={{background:'linear-gradient(135deg,#00d4ff,#7b2ff7)',borderRadius:'4px',padding:'4px 8px',textAlign:'center',fontSize:'9px',fontWeight:600,color:'#fff'}}>Complete &amp; Restart</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {i === 3 && (
                              <div className="mt-3 mx-1 rounded-lg border border-slate-700/40 bg-slate-900/60 p-3 flex items-center justify-center">
                                <div className="flex flex-col items-center gap-2">
                                  <div style={{width:'148px',height:'64px',background:'#ffffff',borderRadius:'4px',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                    <div style={{textAlign:'center',fontFamily:'monospace'}}>
                                      <div style={{fontSize:'13px',fontWeight:700,color:'#1a1a1a'}}>✓ Success</div>
                                      <div style={{fontSize:'8px',color:'#444'}}>My Stream Stand</div>
                                      <div style={{fontSize:'10px',color:'#1a1a1a',fontWeight:600}}>192.168.1.50</div>
                                    </div>
                                  </div>
                                  <span style={{fontSize:'0.6rem',color:'#94a3b8'}}>Example IP Address</span>
                                </div>
                              </div>
                            )}
                            {i === 4 && (
                              <div className="mt-3 mx-1 rounded-lg overflow-hidden border border-slate-700/40 bg-slate-900/60 p-3 flex flex-col items-center gap-2">
                                <div className="relative overflow-hidden rounded-lg border border-slate-700/50" style={{width:'296px', height:'128px', background:'#0f172a'}}>
                                  {[1,2,3,4,5].map((n) => (
                                    <img
                                      key={n}
                                      src={`/display${n}.png`}
                                      alt={`Display example ${n}`}
                                      className="absolute inset-0 w-full h-full object-cover"
                                      style={{
                                        opacity: displayImageIndex === n - 1 ? 1 : 0,
                                        filter: 'url(#epaper)',
                                        transition: 'opacity 0.7s ease',
                                      }}
                                    />
                                  ))}
                                </div>
                                <span style={{fontSize:'0.6rem',color:'#94a3b8'}}>Push graphics, text, or a combination of both to your display from any device on your network</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* One-time note */}
                      <div className="mt-3 flex items-center gap-2 px-1">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        <p className="text-slate-500 text-[11px] leading-relaxed">You only need to do this once — next time you power on, your device will connect automatically. Just power on and visit your device IP.</p>
                      </div>

                      {/* Factory reset note */}
                      <div className="mt-2 flex items-center gap-2 px-1">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <p className="text-slate-500 text-[11px] leading-relaxed">To factory reset, hold the reset button whilst powering on for 5 seconds. This will clear your Wi-Fi settings and restart setup.</p>
                      </div>

                      {/* Troubleshooting */}
                      <div className="mt-2 px-1">
                        <a href="https://pokebubble.com/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-150 active:scale-95" style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)'}}>
                          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'rgba(239,68,68,0.15)'}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                          </div>
                          <p className="text-slate-300 text-[11px] leading-relaxed">Need help using this device? Support is available via <span className="text-cyan-400">Discord</span>.</p>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Update Firmware bar */}
                  <div className="border-t border-slate-700/50" />
                  <div className="px-4 py-2 flex items-center justify-between cursor-pointer" onClick={() => { setExpandedFirmware(expandedFirmware === item.id ? null : item.id); setExpandedInstructions(null); }}>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                        </svg>
                      </div>
                      <div className="w-px h-6 bg-slate-700/60 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-slate-300">Update Firmware</span>
                        <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Keep your device up to date with the latest features.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="font-mono" style={{fontSize:'0.55rem', color:'#4ade80'}}>Latest: {LATEST_VERSION}</span>
                        {deviceVersion && <span className="font-mono" style={{fontSize:'0.55rem', color:'#94a3b8'}}>{deviceName}: {deviceVersion}</span>}
                      </div>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)', transform: expandedFirmware === item.id ? 'rotate(90deg)' : 'rotate(0deg)'}}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-slate-700/50" />

                  <div className={`instructions-body${expandedFirmware === item.id ? ' open' : ''}`}>
                    <div className="instructions-inner">
                      <div className="px-4 pt-4 pb-4">
                      <div className="rounded-xl border border-slate-700/40 overflow-hidden">

                        {/* State: unsupported */}
                        {firmwareState === 'unsupported' && (
                          <div className="px-4 py-4 flex flex-col gap-2">
                            <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.25)'}}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                              <div>
                                <p className="text-amber-400 text-xs font-semibold">Browser not supported</p>
                                <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Please use Chrome or Edge to use this feature.</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* State: disconnected */}
                        {firmwareState === 'disconnected' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between bg-slate-900/60 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-slate-600 flex-shrink-0"/>
                                <span className="text-slate-500 text-xs font-mono">Not connected</span>
                              </div>
                              <span className="text-slate-600 font-mono" style={{fontSize:'0.6rem'}}>Current: v1.2.4</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500" style={{fontSize:'0.65rem'}}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              Plug your stand into your computer via USB, then click connect.
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); connectSerial(); }}
                              className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95 flex items-center justify-center gap-2"
                              style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              Connect Device via USB
                            </button>
                          </div>
                        )}

                        {/* State: connecting */}
                        {firmwareState === 'connecting' && (
                          <div className="px-4 py-4 flex flex-col items-center gap-3">
                            <svg className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.15"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                            <div className="text-center">
                              <p className="text-slate-300 text-xs font-semibold">Connecting…</p>
                              <p className="text-slate-600 mt-0.5" style={{fontSize:'0.6rem'}}>Reading device info via serial</p>
                            </div>
                          </div>
                        )}

                        {/* State: connected */}
                        {firmwareState === 'connected' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between bg-slate-900/60 rounded-lg px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-400" style={{boxShadow:'0 0 6px rgba(74,222,128,0.6)'}}/>
                                <span className="text-white text-xs font-semibold">{deviceName || 'Digital Display Stand'}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-slate-500 font-mono" style={{fontSize:'0.6rem'}}>Digital Display Stand</p>
                                <p className="text-cyan-400 font-mono font-semibold" style={{fontSize:'0.6rem'}}>{deviceVersion || 'v1.0.0'}</p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setFirmwareState('checking');
                                setTimeout(() => {
                                  const isLatest = deviceVersion === LATEST_VERSION;
                                  setFirmwareState(isLatest ? 'uptodate' : 'available');
                                }, 2000);
                              }}
                              className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                              style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}
                            >Check for Updates</button>
                            <button onClick={(e) => { e.stopPropagation(); disconnectSerial(); }} className="text-slate-600 text-[10px] text-center w-full hover:text-slate-400 transition-colors">Disconnect</button>
                          </div>
                        )}

                        {/* State: checking */}
                        {firmwareState === 'checking' && (
                          <div className="px-4 py-4 flex flex-col items-center gap-3">
                            <svg className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.15"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                            <div className="text-center">
                              <p className="text-slate-300 text-xs font-semibold">Checking for updates…</p>
                              <p className="text-slate-600 mt-0.5" style={{fontSize:'0.6rem'}}>cheeks.uk/firmware/esp32c6</p>
                            </div>
                          </div>
                        )}

                        {/* State: up to date */}
                        {firmwareState === 'uptodate' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.2)'}}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              <div>
                                <p className="text-green-400 text-xs font-semibold">Your firmware is up to date</p>
                                <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>{LATEST_VERSION} — latest release</p>
                              </div>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-slate-600 text-[10px] text-center w-full hover:text-slate-400 transition-colors">Back</button>
                          </div>
                        )}

                        {/* State: update available */}
                        {firmwareState === 'available' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.2)'}}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.96"/></svg>
                              <div className="flex-1">
                                <p className="text-cyan-400 text-xs font-semibold">Update available — {LATEST_VERSION}</p>
                                <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Released 3 Apr 2026 · 284 KB</p>
                              </div>
                            </div>
                            <div className="text-slate-500 bg-slate-900/40 rounded-lg px-3 py-2" style={{fontSize:'0.6rem'}}>
                              <p className="text-slate-400 font-semibold mb-1">What's new in {LATEST_VERSION}</p>
                              <p>• Faster Wi-Fi reconnection on boot</p>
                              <p>• Stream Deck stability improvements</p>
                              <p>• Fixed rare display flicker on update</p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); runOTA(); }}
                              className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                              style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}
                            >Install Update</button>
                            <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-slate-600 text-[10px] text-center w-full hover:text-slate-400 transition-colors">Cancel</button>
                          </div>
                        )}

                        {/* State: updating */}
                        {firmwareState === 'updating' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-300 text-xs font-semibold">Installing {LATEST_VERSION}…</span>
                              <span className="text-cyan-400 font-mono text-xs">{Math.round(firmwareProgress)}%</span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-slate-700/60 overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-200" style={{width:`${firmwareProgress}%`, background:'linear-gradient(90deg,#22d3ee,#3b82f6)'}}/>
                            </div>
                            <p className="text-slate-600 text-[10px]">Do not unplug your device during this process.</p>
                          </div>
                        )}

                        {/* State: done */}
                        {firmwareState === 'done' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.2)'}}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              <div>
                                <p className="text-green-400 text-xs font-semibold">Successfully updated to {LATEST_VERSION}</p>
                                <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Device is restarting — this may take a few seconds.</p>
                              </div>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); disconnectSerial(); }} className="text-slate-600 text-[10px] text-center w-full hover:text-slate-400 transition-colors">Done</button>
                          </div>
                        )}

                        {/* State: error */}
                        {firmwareState === 'error' && (
                          <div className="px-4 py-4 flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(251,113,133,0.08)', border:'1px solid rgba(251,113,133,0.2)'}}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fb7185" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              <div>
                                <p className="text-rose-400 text-xs font-semibold">Update failed</p>
                                <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Check your device is connected to Wi-Fi and try again.</p>
                              </div>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-slate-600 text-[10px] text-center w-full hover:text-slate-400 transition-colors">Try again</button>
                          </div>
                        )}

                      </div>
                      {/* Discord firmware notification note */}
                      <div className="mt-3 px-1 pb-1">
                        <a href="https://pokebubble.com/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-150 active:scale-95" style={{background:'rgba(234,179,8,0.08)', border:'1px solid rgba(234,179,8,0.2)'}}>
                          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'rgba(234,179,8,0.15)'}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'bellRing 2s ease-in-out infinite', transformOrigin:'top center'}}>
                              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                          </div>
                          <p className="text-slate-300 text-[11px] leading-relaxed">Be notified on <span className="text-cyan-400">Discord</span> of the latest firmware updates.</p>
                        </a>
                      </div>
                    </div>
                  </div>
                  </div>

              </div>
            ))}

            {/* ── ADJUSTABLE STAND CARD ── */}
            <div className="creation-card bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">
              <div className="relative w-full overflow-hidden" style={{aspectRatio:'1/1', background:'radial-gradient(ellipse at 60% 50%, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.06) 40%, rgba(15,23,42,0.0) 70%)'}}>
                <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(59,130,246,0.06), transparent)', zIndex:0}} />
                <img src="/stand_adjustable.jpeg" alt="Custom Card & Slab Stand (Adjustable)" className="w-full h-full object-cover" style={{position:'relative', zIndex:1}} />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #22d3ee, #3b82f6)'}}>
                  New
                </div>
              </div>
              <div className="px-4 pt-4 pb-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Custom Card & Slab Stand (Adjustable)</h2>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-slate-500 text-[10px] leading-tight text-right">MOQ<br/><span className="text-slate-400 font-semibold">5 units</span></span>
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                      <span className="text-white font-semibold uppercase tracking-widest" style={{fontSize:'0.55rem'}}>From</span>
                      <span className="text-white font-bold text-base leading-tight">£19.99</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['3D Printed','Adjustable Fit','All TCGs','Slab & Raw Cards','Custom Colours'].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span className="text-slate-300 font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 leading-relaxed mb-0" style={{fontSize:'0.75rem'}}>
                  A 3D printed display stand with an adjustable cradle to fit cards and slabs of varying sizes. Compatible with all major TCGs — Pokémon, MTG, Yu-Gi-Oh! and more. Print in your choice of filament colour to match your setup.
                </p>
              </div>
            </div>

            {/* ── FIXED STAND CARD ── */}
            <div className="creation-card bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">
              <div className="relative w-full overflow-hidden" style={{aspectRatio:'1/1', background:'radial-gradient(ellipse at 60% 50%, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.06) 40%, rgba(15,23,42,0.0) 70%)'}}>
                <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(59,130,246,0.06), transparent)', zIndex:0}} />
                <img src="/stand_fixed.jpeg" alt="Custom Card & Slab Stand (Fixed)" className="w-full h-full object-cover" style={{position:'relative', zIndex:1}} />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #22d3ee, #3b82f6)'}}>
                  New
                </div>
              </div>
              <div className="px-4 pt-4 pb-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Custom Card & Slab Stand (Fixed)</h2>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-slate-500 text-[10px] leading-tight text-right">MOQ<br/><span className="text-slate-400 font-semibold">10 units</span></span>
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                      <span className="text-white font-semibold uppercase tracking-widest" style={{fontSize:'0.55rem'}}>From</span>
                      <span className="text-white font-bold text-base leading-tight">£14.99</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['3D Printed','Fixed Cradle','All TCGs','Slab & Raw Cards','Custom Colours'].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span className="text-slate-300 font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 leading-relaxed mb-0" style={{fontSize:'0.75rem'}}>
                  A clean, minimal 3D printed display stand with a fixed cradle — perfectly sized for your cards or slabs. Solid, sturdy and simple. Compatible with all major TCGs and available in any filament colour you like.
                </p>
              </div>
            </div>

            {/* ── RUGGED BOX CARD ── */}
            <div className="creation-card bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">

              {/* Product image */}
              <div className="relative w-full overflow-hidden" style={{aspectRatio:'4/3'}}>
                <img
                  src="/ruggedbox.jpg"
                  alt="Semi Rigid Card Sleeve Rugged Box"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #22d3ee, #3b82f6)'}}>
                  Free Model
                </div>
              </div>

              {/* Info block */}
              <div className="px-4 pt-4 pb-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Semi Rigid Card Sleeve Rugged Box</h2>
                  <div className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                    <span className="text-white font-bold text-base leading-tight">FREE</span>
                  </div>
                </div>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    'Free 3D Print',
                    '100–120 Cards',
                    'Semi Rigid Sleeves',
                    'PLA or PETG',
                    'Snap-Shut Lid',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="text-slate-300 font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <p className="text-slate-400 leading-relaxed mb-4" style={{fontSize:'0.75rem'}}>
                  Designed to fit semi rigid sleeves perfectly and holds 100–120 sleeved cards. Prints well in PLA, PLA Matte or PETG if you want a bit more durability. Secure the lid with M3 screws and locking fasteners, or use 3mm rod / filament pins instead. Just avoid matte filament for the pins — they're prone to snapping.
                </p>

              </div>

              {/* MakerWorld download bar */}
              <div className="border-t border-slate-700/50" />
              <a
                href="https://makerworld.com/en/models/1573714-semi-rigid-card-sleeve-rugged-box"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </div>
                  <div className="w-px h-6 bg-slate-700/60 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-slate-300">Download Free on MakerWorld</span>
                    <p className="text-slate-500 mt-0.5" style={{fontSize:'0.6rem'}}>Get the free 3D print file for this model.</p>
                  </div>
                </div>
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </a>
              <div className="border-t border-slate-700/50" />
            </div>

            {/* More creations coming soon */}
            <div className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/40 rounded-2xl px-4 py-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}>
                <img src="/pball.svg" alt="" className="h-4 w-4 animate-spin" style={{animationDuration:"2s", filter:"brightness(0) invert(1)"}} />
              </div>
              <div className="w-px h-6 bg-slate-700/60 flex-shrink-0" />
              <div>
                <p className="text-slate-300 text-xs font-semibold leading-none">More creations on the way</p>
                <p className="text-slate-500 text-[10px] mt-0.5 leading-none">New products are being added to this page shortly.</p>
              </div>
            </div>

          </div>
        </div>
        </main>
      )}

      {/* ── INVENTORY LIGHTBOX ── */}
      {selectedInventoryImage && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center p-4"
          style={{background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)'}}
          onClick={() => setSelectedInventoryImage(null)}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-slate-700/50"
            style={{background:'#0f172a'}}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedInventoryImage(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              style={{background:'rgba(15,23,42,0.9)', border:'1px solid rgba(51,65,85,0.6)'}}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Image */}
            <div className="relative w-full" style={{aspectRatio:'1/1'}}>
              <img
                src={`/${selectedInventoryImage.folder}/${selectedInventoryImage.filename}`}
                alt={selectedInventoryImage.name}
                className="w-full h-full object-contain"
              />
              {/* Grade badge */}
              {selectedInventoryImage.grade && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-2.5 py-1 rounded-md text-xs font-bold shadow-lg border border-cyan-400/50">
                  {selectedInventoryImage.grade}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="px-4 py-3 border-t border-slate-700/50">
              <h3 className="text-white font-bold text-sm leading-tight">{selectedInventoryImage.name}</h3>
              {selectedInventoryImage.set.split('  ').map((line, idx) => (
                <p key={idx} className="text-slate-400 text-xs mt-0.5 leading-tight">{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer className="bg-slate-800/80 backdrop-blur-lg border-t border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-3">
              <a href="https://pokebubble.com/docs" className="group">
                <img src="/discord.png" alt="Discord" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="group">
                <img src="/instagram.png" alt="Instagram" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <button onClick={() => setShowVideoModal(true)} className="group">
                <img src="/wnlogo.png" alt="Whatnot" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </button>
            </div>
            <span className="text-slate-600 text-xs">© 2026 PokéBubble</span>
          </div>
        </div>
      </footer>

      {/* ── WHATNOT VIDEO MODAL ── */}
      {showVideoModal && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-[200] pointer-events-none">
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-md max-h-[95vh] overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/50 pointer-events-auto">
            <button
              onClick={handleVideoModalClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-slate-700/80 hover:bg-slate-600/80 text-slate-300 hover:text-white rounded-full flex items-center justify-center transition-colors duration-200 backdrop-blur-sm border border-slate-600/50"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="relative">
              <video
                src="/splash.mp4"
                autoPlay
                muted
                preload="auto"
                onEnded={handleVideoEnd}
                className="w-full h-auto rounded-2xl object-cover"
                style={{ aspectRatio: '9/16' }}
              >
                Your browser does not support the video tag.
              </video>
              <div className={`absolute top-6 left-4 right-4 flex justify-center transition-all duration-1000 ease-out ${videoEnded ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-sm">
                  {/* Banner image with stats bar overlaid at bottom, right-aligned so avatar doesn't hide them */}
                  <div className="relative h-36 overflow-hidden">
                    <img src="https://rare-gallery.com/uploads/posts/1066121-illustration-anime-text-logo-cartoon-Pok-mon-Togepi-brand-ishmam-computer-wallpaper-font-organ.png" alt="Banner" className="w-full h-full object-cover object-center" />
                    {/* Stats bar pinned to bottom-right */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-3 py-1.5 flex items-center justify-end gap-3">
                      <span className="flex items-center gap-1 text-white text-xs font-semibold"><span className="text-yellow-400">★</span>5.0</span>
                      <span className="text-white/30">|</span>
                      <span className="text-white/60 text-xs">Reviews <span className="text-white font-semibold">182</span></span>
                      <span className="text-white/30">|</span>
                      <span className="text-white/60 text-xs">Sold <span className="text-white font-semibold">1.1k</span></span>
                    </div>
                  </div>
                  {/* Avatar sitting on the border between banner and white, not clipped by overflow-hidden */}
                  <div className="relative px-4">
                    <div className="absolute -top-7 left-4 w-14 h-14 rounded-full border-[3px] border-white overflow-hidden bg-white shadow-xl">
                      <img src="/pfp.png" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    {/* Button top-right of white area */}
                    <div className="flex justify-end pt-2">
                      <a href="https://www.whatnot.com/en-GB/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); window.open('https://www.whatnot.com/en-GB/user/togecheeks/shows', '_blank'); e.preventDefault(); }} className={`flex items-center gap-1.5 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 active:scale-95 shadow-md flex-shrink-0 ${isLive ? 'bg-red-500 hover:bg-red-600' : 'bg-black hover:bg-slate-800'}`}>
                        {isLive ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>
                            Watch Now
                          </>
                        ) : (
                          <>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            View Profile
                          </>
                        )}
                      </a>
                    </div>
                    {/* Name + handle */}
                    <div className="pt-1 pb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-black font-bold text-lg leading-none">cheeks</span>
                        {isLive ? (
                          <span className="flex items-center gap-1 bg-red-500 text-white text-[0.55rem] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>LIVE
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 bg-slate-200 text-slate-500 text-[0.55rem] font-semibold px-1.5 py-0.5 rounded-full leading-none">OFFLINE</span>
                        )}
                      </div>
                      <span className="text-black/60 text-xs font-medium leading-none block mt-0.5">@togecheeks</span>
                      <p className="text-slate-500 text-[0.65rem] leading-relaxed mt-1.5">Everything Pokémon — cards, collectibles & more ✨</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`absolute bottom-6 left-4 right-4 transition-all duration-1000 ease-out ${videoEnded ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
                <div className="space-y-3">
                  <a href="https://www.whatnot.com/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><Bookmark className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Bookmark the next Livestreams</span>
                  </a>
                  <a href="https://whatnot.com/invite/togecheeks" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><Gift className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Get £10 FREE Pokemon Cards</span>
                  </a>
                  <a href="https://whatnot.com/invite/seller/togecheeks" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><DollarSign className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Become a Seller and get £150 FREE</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── POKÉMON GO MODAL ── */}
      {showPogoModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)'}} onClick={() => setShowPogoModal(false)}>
          <div className="bg-slate-800 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-xs p-5" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img src="/pogo.png" alt="Pokémon GO" className="w-7 h-7 object-contain" />
                <span className="text-white font-bold text-base">Pokémon GO</span>
              </div>
              <button onClick={() => setShowPogoModal(false)} className="text-slate-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Trainer name — centred title */}
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <img src="/valor.webp" alt="Team Valor" className="w-4 h-4 object-contain" />
              <span className="text-white font-bold text-sm">togecheeks</span>
              <span className="text-slate-400 font-medium text-xs">Lv. 70</span>
            </div>
            {/* Trainer code box */}
            <div className="bg-slate-900/60 rounded-xl border border-slate-700/50 p-3 mb-3 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-medium" style={{fontSize:'0.7rem'}}>Trainer Code</span>
                <div className="relative flex items-center" style={{height:'1rem'}}>
                  <span className={`absolute right-0 flex items-center gap-1 transition-all duration-500 ${showCopiedToast ? 'opacity-100' : 'opacity-0'}`}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-green-400 font-semibold whitespace-nowrap" style={{fontSize:'0.7rem'}}>Trainer Code Copied</span>
                  </span>
                  <span className={`transition-all duration-500 ${showCopiedToast ? 'opacity-0' : 'opacity-100'} text-white font-semibold font-mono whitespace-nowrap`} style={{fontSize:'0.7rem'}}>6644 6392 9998</span>
                </div>
              </div>
            </div>
            {/* QR code */}
            <div className="flex flex-col items-center gap-2">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=664463929998&bgcolor=1e293b&color=ffffff&margin=10`}
                alt="QR Code"
                className="rounded-xl w-44 h-44"
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CardExplorer;